import React from "react";
import { toast } from "sonner";
import { Upload, Trash2, ArrowUp, ArrowDown } from "lucide-react";
import { endpoints } from "../lib/api";

export function AdminGallery({ canEdit }) {
  const [photos, setPhotos] = React.useState(null);
  const [busy, setBusy] = React.useState("");
  const fileRef = React.useRef();
  const load = () => endpoints.gallery().then((d) => setPhotos(d.photos)).catch(() => setPhotos([]));
  React.useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    let ok = 0;
    for (let i = 0; i < files.length; i++) {
      setBusy(`Uploading ${i + 1} of ${files.length}…`);
      try { await endpoints.adminGalleryUpload(files[i]); ok++; }
      catch { toast(`Couldn't upload ${files[i].name}`); }
    }
    setBusy("");
    if (ok) toast(`${ok} photo${ok > 1 ? "s" : ""} added to the gallery`);
    load();
  };
  const saveCaption = async (p, caption) => {
    if (caption === p.caption) return;
    try {
      await endpoints.adminGalleryUpdate(p.id, { caption });
      setPhotos((list) => list.map((x) => (x.id === p.id ? { ...x, caption } : x)));
      toast("Caption saved");
    } catch { toast("Couldn't save caption"); }
  };
  const move = async (i, d) => {
    const list = [...photos];
    const j = i + d;
    if (j < 0 || j >= list.length) return;
    [list[i], list[j]] = [list[j], list[i]];
    setPhotos(list);
    try { await Promise.all(list.map((p, k) => endpoints.adminGalleryUpdate(p.id, { order: k }))); }
    catch { toast("Couldn't save the new order"); load(); }
  };
  const remove = async (p) => {
    if (!window.confirm("Remove this photo from the gallery?")) return;
    try { await endpoints.adminGalleryDelete(p.id); toast("Photo removed"); }
    catch { toast("Couldn't remove photo"); }
    load();
  };

  return (
    <div data-testid="admin-gallery">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <p className="text-[14px] text-on-dark-muted">Photos appear in the "Wrap-up" section of the <a href="/winners" className="text-gold-400 underline">Winners page</a>, in this order. Max 15 MB each.</p>
        {canEdit && <>
          <input ref={fileRef} type="file" accept="image/*" multiple onChange={upload} className="hidden" data-testid="gallery-file-input" />
          <button onClick={() => fileRef.current.click()} disabled={!!busy} data-testid="gallery-upload-btn"
            className="flex items-center gap-2 rounded-pill bg-gold-500 px-5 py-2.5 font-bold text-navy-900 disabled:opacity-60">
            <Upload size={16} /> {busy || "Upload photos"}
          </button>
        </>}
      </div>
      {!photos ? <p className="text-on-dark-muted">Loading…</p> : photos.length === 0 ? (
        <p className="py-10 text-center text-on-dark-muted" data-testid="gallery-empty">No photos yet. Upload a few to fill the wrap-up gallery.</p>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {photos.map((p, i) => (
            <div key={p.id} className="overflow-hidden rounded-lg bg-white/5 ring-1 ring-white/10" data-testid={`admin-gallery-item-${i}`}>
              <img src={p.url} alt="" className="aspect-[4/3] w-full object-cover" />
              <div className="space-y-2 p-2">
                <input defaultValue={p.caption} disabled={!canEdit} placeholder="Add a caption" onBlur={(e) => saveCaption(p, e.target.value)}
                  data-testid={`gallery-caption-${i}`} className="w-full rounded-md border border-white/15 bg-navy-800 px-2 py-1.5 text-[13px] text-white" />
                {canEdit && <div className="flex items-center justify-between">
                  <div className="flex gap-1">
                    <button onClick={() => move(i, -1)} disabled={i === 0} aria-label="Move earlier" data-testid={`gallery-up-${i}`} className="rounded bg-white/10 p-1.5 text-white disabled:opacity-30"><ArrowUp size={14} /></button>
                    <button onClick={() => move(i, 1)} disabled={i === photos.length - 1} aria-label="Move later" data-testid={`gallery-down-${i}`} className="rounded bg-white/10 p-1.5 text-white disabled:opacity-30"><ArrowDown size={14} /></button>
                  </div>
                  <button onClick={() => remove(p)} aria-label="Remove photo" data-testid={`gallery-delete-${i}`} className="rounded bg-coral-500/15 p-1.5 text-coral-500"><Trash2 size={14} /></button>
                </div>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
